import React from "react";
import PropTypes from "prop-types";
import { compose } from "redux";
import PDPCarousel from "components/source/pdp/pdp-carousel";
import PDPOutfitStyles from "./pdp-outfit-styles";
import sharedActions from "actions/shared-actions";
import carouselTypes from "helpers/carousel-types";
import {
  flagsAndExperimentsPropType,
  flagsAndExperimentNames,
  withOnLoadFlagsAndExperiments,
} from "components/source/hoc/with-flags-and-experiments";
import { getUserId } from "components/source/hoc/with-user-data";
import { NEXT_GRID_TYPES, PRODUCT_SCOPES } from "rtr-constants";
import productsActions from "actions/products-actions";
import { DiscoSearchServiceClient } from "clients/disco-search-service-client";
import { getUserDataLens, isSubscriptionLens } from "helpers/membership-helpers";
import MembershipUpgradesHelpers from "helpers/membership-upgrades-helpers";
import { productPropType, userDataPropType } from "components/propTypes";
import { connect } from "react-redux";
import { withFeatureFlags, featureFlagsPropType, Flags } from "components/source/hoc/with-feature-flags";
import { mapDiscoProductToCarouselProduct } from "helpers/disco-filter-helpers-ts";
import { getAttributeDisplayName, mapGridQueryToDiscoQuery } from "helpers/disco-filter-helpers";
import ProductCarousel from "components/source/shared/carousels/product-carousel";

class PDPCarousels extends React.Component {
  static propTypes = {
    dispatch: PropTypes.func.isRequired,
    product: productPropType,
    isAccessory: PropTypes.bool,
    userData: userDataPropType,
    featureFlags: featureFlagsPropType,
    flagsAndExperiments: flagsAndExperimentsPropType,
    workingFilters: PropTypes.object,
    annotationSuffix: PropTypes.string,
    isRentable: PropTypes.bool,
    isUPDP: PropTypes.bool,
    // membershipState is used in MembershipUpgradesHelpers.shouldRefreshUpgradePricingCe
    // eslint-disable-next-line react/no-unused-prop-types
    membershipState: PropTypes.object,
    shortlists: PropTypes.arrayOf(PropTypes.object),
    displayedModal: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    collectionCarouselSuccess: PropTypes.shape({
      title: PropTypes.string,
      pixelName: PropTypes.string,
      type: PropTypes.string,
      products: PropTypes.arrayOf(PropTypes.object),
      url: PropTypes.string,
    }),
    recommendationCarouselSuccess: PropTypes.shape({
      title: PropTypes.string,
      pixelName: PropTypes.string,
      type: PropTypes.string,
      products: PropTypes.arrayOf(PropTypes.object),
      url: PropTypes.string,
    }),
    rentTheLookSuccess: PropTypes.shape({
      title: PropTypes.string,
      pixelName: PropTypes.string,
      type: PropTypes.string,
      products: PropTypes.arrayOf(PropTypes.object),
    }),
    similarStylesSuccess: PropTypes.shape({
      title: PropTypes.string,
      pixelName: PropTypes.string,
      type: PropTypes.string,
      products: PropTypes.arrayOf(PropTypes.object),
      url: PropTypes.string,
    }),
    youMayAlsoLikeSuccess: PropTypes.shape({
      title: PropTypes.string,
      pixelName: PropTypes.string,
      type: PropTypes.string,
      products: PropTypes.arrayOf(PropTypes.object),
      url: PropTypes.string,
    }),
    clearanceCarouselSuccess: PropTypes.shape({
      title: PropTypes.string,
      pixelName: PropTypes.string,
      type: PropTypes.string,
      products: PropTypes.arrayOf(PropTypes.object),
    }),
    designerRecommendationsSuccess: PropTypes.shape({
      title: PropTypes.string,
      pixelName: PropTypes.string,
      type: PropTypes.string,
      products: PropTypes.arrayOf(PropTypes.object),
    }),
    similarDesignersRecommendationsSuccess: PropTypes.shape({
      title: PropTypes.string,
      pixelName: PropTypes.string,
      type: PropTypes.string,
      products: PropTypes.arrayOf(PropTypes.object),
    }),
    stylePersonaRecommendationsSuccess: PropTypes.shape({
      title: PropTypes.string,
      pixelName: PropTypes.string,
      type: PropTypes.string,
      products: PropTypes.arrayOf(PropTypes.object),
    }),
    necklineRecommendationsSuccess: PropTypes.shape({
      title: PropTypes.string,
      pixelName: PropTypes.string,
      type: PropTypes.string,
      products: PropTypes.arrayOf(PropTypes.object),
    }),
    embellishmentRecommendationsSuccess: PropTypes.shape({
      title: PropTypes.string,
      pixelName: PropTypes.string,
      type: PropTypes.string,
      products: PropTypes.arrayOf(PropTypes.object),
    }),
    fetchRentTheLookCarousel: PropTypes.func,
    fetchSimilarStylesCarousel: PropTypes.func,
    fetchYouMayAlsoLikeCarousel: PropTypes.func,
    fetchCollectionCarousel: PropTypes.func,
    fetchRecommendationCarousel: PropTypes.func,
    fetchClearanceCarousel: PropTypes.func,
    authDataHasLoaded: PropTypes.bool,
    staticProduct: PropTypes.object,
    merchandisingCategory: PropTypes.string,
    pdpCarouselList: PropTypes.arrayOf(PropTypes.object),
    fetchDesignerRecommendationsCarousel: PropTypes.func,
    fetchSimilarDesignersRecommendationsCarousel: PropTypes.func,
    fetchStylePersonaRecommendationsCarousel: PropTypes.func,
    fetchNecklineRecommendationsCarousel: PropTypes.func,
    fetchEmbellishmentRecommendationsCarousel: PropTypes.func,
  };

  state = {
    recentlyViewedCarouselItems: [],
  };
  /*
    Heads Up! This component is being shared with Universal PDP, 
    Ensure all changes made here work well Universal PDP
  */

  //If workingFilters and authData are available on mount, call carousels immediately
  //This would typically be when the user interacts with the PDP Drawer on grids
  componentDidMount() {
    const { authDataHasLoaded, workingFilters, pdpCarouselList, isUPDP } = this.props;
    if (authDataHasLoaded && workingFilters && (pdpCarouselList?.length || isUPDP)) {
      this.fetchCarousels();
    }
  }

  //If we did not have workingFilters and authData on mount, or we need to update pricing, call carousels
  //This would typically be when the user loads a full PDP
  componentDidUpdate(prevProps) {
    const { authDataHasLoaded, pdpCarouselList, workingFilters, isUPDP } = this.props;

    const preRequisiteState = (pdpCarouselList?.length || isUPDP) && authDataHasLoaded && workingFilters;
    const shouldRefreshUpgradePricing = MembershipUpgradesHelpers.shouldRefreshUpgradePricingCe(prevProps, this.props);
    const stateCheck =
      preRequisiteState && (!prevProps.workingFilters || !prevProps.authDataHasLoaded || shouldRefreshUpgradePricing);

    if (stateCheck) {
      this.fetchCarousels(shouldRefreshUpgradePricing);
    }
  }

  isClearanceCategory() {
    return this.props.merchandisingCategory === "Clearance";
  }

  closeModal = () => {
    this.props.dispatch(sharedActions.displayModal(false));
  };

  fetchCarousels(isUpgradePricingRefresh) {
    const {
      fetchCollectionCarousel,
      fetchSimilarStylesCarousel,
      fetchRentTheLookCarousel,
      fetchYouMayAlsoLikeCarousel,
      fetchRecommendationCarousel,
      fetchDesignerRecommendationsCarousel,
      fetchSimilarDesignersRecommendationsCarousel,
      fetchStylePersonaRecommendationsCarousel,
      fetchNecklineRecommendationsCarousel,
      fetchEmbellishmentRecommendationsCarousel,
      fetchClearanceCarousel,
      userData,
      workingFilters,
      youMayAlsoLikeSuccess,
      staticProduct,
    } = this.props;
    const productId = staticProduct?.productId;
    const category = staticProduct?.category;
    const additionalAttributes = staticProduct?.additionalAttributes;

    if (this.isClearanceCategory()) {
      fetchClearanceCarousel(category);
      fetchSimilarDesignersRecommendationsCarousel({ scopeId: staticProduct?.designerId }, `Similar designers`, true);
    } else {
      if (this.shouldFetchOutfits()) {
        fetchRentTheLookCarousel(productId);
      }
      if (this.shouldFetchSimilarStyles() || isUpgradePricingRefresh) {
        const color = additionalAttributes?.find(attr => attr.id === "color")?.values;
        const colorDisplayName = color ? getAttributeDisplayName(color[0]) : "";
        const categoryDisplayText = category ? getAttributeDisplayName(category) : "";
        const similarStylesTitle = `More ${colorDisplayName} ${categoryDisplayText}`;
        fetchSimilarStylesCarousel(productId, userData, workingFilters, similarStylesTitle);
      }
      if (!youMayAlsoLikeSuccess || isUpgradePricingRefresh) {
        fetchYouMayAlsoLikeCarousel(productId, userData, workingFilters);
      }

      fetchCollectionCarousel("new_arrivals_app", category);
      // Create a recommendations carousel for simialr occasions
      if (category && Array.isArray(additionalAttributes) && additionalAttributes.length > 0) {
        const useCaseEventId = "useCaseEvent";
        const stylePersonaId = "stylePersona";
        const necklineId = "neckline";
        const embellishmentId = "embellishment";
        const useCaseEvent = additionalAttributes?.find(attr => attr.id === useCaseEventId)?.values;
        const stylePersona = additionalAttributes?.find(attr => attr.id === stylePersonaId)?.values;
        const neckline = additionalAttributes?.find(attr => attr.id === necklineId)?.values;
        const embellishment = additionalAttributes?.find(attr => attr.id === embellishmentId)?.values;
        const categoryDisplayName = category.replace(/([A-Z])/g, " $1").trim();

        fetchRecommendationCarousel({ category, useCaseEvent: useCaseEvent });
        fetchDesignerRecommendationsCarousel(
          { designerId: staticProduct?.designerId },
          `More from ${staticProduct?.designerDisplayName}`
        );

        fetchSimilarDesignersRecommendationsCarousel({ scopeId: staticProduct?.designerId }, `Similar designers`);

        if (stylePersona && !stylePersona.includes("None")) {
          fetchStylePersonaRecommendationsCarousel({ category, stylePersona }, `Similar styles`);
        }

        if (neckline && !neckline.includes("None")) {
          const necklineDisplayName = getAttributeDisplayName(neckline[0]);
          fetchNecklineRecommendationsCarousel(
            { category, neckline },
            `More ${necklineDisplayName} ${categoryDisplayName}`
          );
        }

        if (embellishment && !embellishment.includes("None")) {
          const embellishmentDisplayName = getAttributeDisplayName(embellishment[0]);
          fetchEmbellishmentRecommendationsCarousel(
            { category, embellishment },
            `More ${embellishmentDisplayName} ${categoryDisplayName}`
          );
        }
      }

      if (this.props?.flagsAndExperiments[flagsAndExperimentNames.SACT_SF_SACT_567_RECENTLY_VIEWED_PDP_CAROUSEL]) {
        const discoQuery = mapGridQueryToDiscoQuery(
          {
            sort: "recommended",
            availableOnly: false,
            includeEarlyAccess: false,
            programEligibility: "FULL_CLOSET",
            zipCode: workingFilters?.zip_code,
          },
          NEXT_GRID_TYPES["user"]
        );
        if (!isSubscriptionLens(userData) || (!getUserId(userData) && !userData?.authData?.id) || !discoQuery) {
          return;
        }

        DiscoSearchServiceClient.getInstance()
          .userProductSearch(
            discoQuery,
            0,
            60,
            getUserDataLens(userData),
            getUserId(userData) ?? userData.authData.id,
            PRODUCT_SCOPES.RECENTLY_VIEWED,
            true // isActiveMember
          )
          .then(response => {
            if (!response || !Array.isArray(response?.items) || !response.items.length) {
              return;
            }
            this.setState({ recentlyViewedCarouselItems: response.items });
          });
      }
    }
  }

  shouldFetchOutfits = () => {
    const { featureFlags, pdpCarouselList, rentTheLookSuccess, isUPDP } = this.props;

    return (
      !rentTheLookSuccess &&
      featureFlags?.[Flags.PDP_HOW_WE_STYLED_IT] &&
      // since Universal PDP will not have pdpCarouselList
      (isUPDP || pdpCarouselList?.includes(carouselTypes.OUTFITS))
    );
  };

  shouldFetchSimilarStyles = () => {
    const { featureFlags, pdpCarouselList, isUPDP } = this.props;
    return (
      featureFlags?.[Flags.VISUALLY_SIMILAR_TO_FLAG] &&
      // since Universal PDP will not have pdpCarouselList
      (isUPDP || pdpCarouselList?.includes(carouselTypes.VISUALLY_SIMILAR_TO))
    );
  };

  reorderCarouselsByOrder(carouselList, sortingOrder) {
    return [...carouselList].sort((a, b) => sortingOrder.indexOf(a.type) - sortingOrder.indexOf(b.type));
  }

  reorderCarousels(carouselList) {
    const visuallySimilar = carouselList.find(carousel => carousel.type === carouselTypes.VISUALLY_SIMILAR_TO);

    if (!visuallySimilar) return carouselList;

    return this.reorderCarouselsByOrder(carouselList, [
      carouselTypes.VISUALLY_SIMILAR_TO,
      carouselTypes.OUTFITS,
      carouselTypes.SIMILAR_ITEMS,
      carouselTypes.RECENTLY_VIEWED,
      carouselTypes.CLEARANCE,
      carouselTypes.SIMILAR_DESIGNERS_RECOMMENDATIONS,
    ]);
  }

  reorderCarouselsForNoIntent = carouselList => {
    const sortingOrder = [
      carouselTypes.OUTFITS,
      carouselTypes.VISUALLY_SIMILAR_TO,
      carouselTypes.RECOMMENDATION,
      carouselTypes.DESIGNER_RECOMMENDATIONS,
      carouselTypes.SIMILAR_DESIGNERS_RECOMMENDATIONS,
      carouselTypes.STYLE_PERSONA_RECOMMENDATIONS,
      carouselTypes.NECKLINE_RECOMMENDATIONS,
      carouselTypes.EMBELLISHMENT_RECOMMENDATIONS,
      carouselTypes.COLLECTION,
      carouselTypes.SIMILAR_ITEMS,
      carouselTypes.CLEARANCE,
    ];

    const reordered = [...carouselList];
    reordered.sort((a, b) => sortingOrder.indexOf(a.type) - sortingOrder.indexOf(b.type));

    return reordered;
  };

  // this function is specifically for the new disco v2 endpoints to render products to shared/carousels/product-carousel
  renderRecentlyViewedCarousel = () => {
    const { flagsAndExperiments = {} } = this.props;
    if (!flagsAndExperiments[flagsAndExperimentNames.SACT_SF_SACT_567_RECENTLY_VIEWED_PDP_CAROUSEL]) {
      return null;
    } else if (!this.state.recentlyViewedCarouselItems.length) {
      return null;
    }
    return (
      <ProductCarousel
        hideDivider={true}
        path="/products/user/recently_viewed"
        products={this.state.recentlyViewedCarouselItems}
        title="Recently Viewed"
      />
    );
  };

  renderCarousels(carouselList) {
    return (
      <div className="pdp-carousel-wrapper">
        {carouselList.map((relatedProductObject, i) => {
          if (relatedProductObject.type === carouselTypes.OUTFITS) {
            // Outfits carousel differs from the other pdp carousels
            return (
              <PDPOutfitStyles
                key={i}
                products={relatedProductObject.products}
                title={relatedProductObject.title}
                subtitle={relatedProductObject.subtitle}
                pixelName={relatedProductObject.pixelName}
                url={relatedProductObject.url}
                type={relatedProductObject.type}
                annotationSuffix={this.props.annotationSuffix}
                isAccessory={this.props.isAccessory}
                closeModal={this.closeModal}
                dispatch={this.props.dispatch}
                displayedModal={this.props.displayedModal}
                filters={this.props.workingFilters}
                isRentable={this.props.isRentable}
                product={this.props.product}
                productId={this.props.staticProduct?.productId}
                staticProduct={this.props.staticProduct}
                shortlists={this.props.shortlists}
                userData={this.props.userData}
                // Always override img reorder for visually
                noModelImageFirst={true}
                isUPDP={this.props.isUPDP}
              />
            );
          } else {
            let carouselProducts;
            const type = relatedProductObject.type;
            if (
              type === carouselTypes.CLEARANCE ||
              type === carouselTypes.COLLECTION ||
              type === carouselTypes.RECOMMENDATION ||
              type === carouselTypes.VISUALLY_SIMILAR_TO ||
              type === carouselTypes.DESIGNER_RECOMMENDATIONS ||
              type === carouselTypes.STYLE_PERSONA_RECOMMENDATIONS ||
              type === carouselTypes.NECKLINE_RECOMMENDATIONS ||
              type === carouselTypes.EMBELLISHMENT_RECOMMENDATIONS ||
              type === carouselTypes.SIMILAR_DESIGNERS_RECOMMENDATIONS
            ) {
              carouselProducts = relatedProductObject?.products?.map(relatedProduct =>
                mapDiscoProductToCarouselProduct(relatedProduct, this.isClearanceCategory())
              );
            } else {
              carouselProducts = relatedProductObject.products;
            }

            return (
              <PDPCarousel
                key={i}
                products={carouselProducts}
                title={relatedProductObject.title}
                pixelName={relatedProductObject.pixelName}
                subtitle={relatedProductObject.subtitle}
                url={relatedProductObject.url}
                type={relatedProductObject.type}
                annotationSuffix={this.props.annotationSuffix}
                closeModal={this.closeModal}
                dispatch={this.props.dispatch}
                displayedModal={this.props.displayedModal}
                filters={this.props.workingFilters}
                isRentable={this.props.isRentable}
                noModelImageFirst={false}
                productId={this.props.staticProduct?.productId}
                shortlists={this.props.shortlists}
                userData={this.props.userData}
                isClearance={this.isClearanceCategory()}
              />
            );
          }
        })}
        {this.renderRecentlyViewedCarousel()}
      </div>
    );
  }

  render() {
    const { rentTheLookSuccess, staticProduct } = this.props;
    const itemToItemCarousels = [
      "collectionCarouselSuccess",
      "recommendationCarouselSuccess",
      "designerRecommendationsSuccess",
      "similarDesignersRecommendationsSuccess",
      "stylePersonaRecommendationsSuccess",
      "necklineRecommendationsSuccess",
      "embellishmentRecommendationsSuccess",
      "similarStylesSuccess",
      "youMayAlsoLikeSuccess",
    ];

    if (staticProduct) {
      // products can sometimes be undefined so need to filter out those values
      const pdpCarousels = [];

      if (rentTheLookSuccess) {
        // default ordering of carousels put RTL carousel beginning of carousel order - AS 6/2/23
        pdpCarousels.unshift(rentTheLookSuccess);
      }

      // Add new arrivals Carousel
      itemToItemCarousels.forEach(prop => {
        const value = this.props[prop];
        if (value) {
          pdpCarousels.push(value);
        }
      });

      // Adds the clearance carousel, if there are more than 5 items in clearance.
      if (this.props.clearanceCarouselSuccess && this.props.clearanceCarouselSuccess?.products?.length > 5) {
        pdpCarousels.push(this.props.clearanceCarouselSuccess);
      }

      if (!pdpCarousels.length && !this.state.recentlyViewedCarouselItems.length) {
        return null;
      }

      // Order should be persistent regardless of intent
      return this.renderCarousels(this.reorderCarouselsForNoIntent(pdpCarousels));
    }
  }
}

const mapStateToProps = (state, ownProps) => {
  return {
    authDataHasLoaded: state.userData?.authDataHasLoaded,
    ceBagCount: state.ceBagCount,
    clearanceCarouselSuccess: state.clearanceCarouselSuccess,
    collectionCarouselSuccess: state.collectionCarouselSuccess,
    designerRecommendationsSuccess: state.designerRecommendationsSuccess,
    similarDesignersRecommendationsSuccess: state.similarDesignersRecommendationsSuccess,
    stylePersonaRecommendationsSuccess: state.stylePersonaRecommendationsSuccess,
    necklineRecommendationsSuccess: state.necklineRecommendationsSuccess,
    displayedModal: state.displayedModal,
    embellishmentRecommendationsSuccess: state.embellishmentRecommendationsSuccess,
    membershipState: state.membershipState,
    merchandisingCategory: state.staticProduct?.merchandisingCategory,
    recommendationCarouselSuccess: state.recommendationCarouselSuccess,
    rentTheLookSuccess: state.rentTheLookSuccess,
    shortlists: state.shortlists,
    similarStylesSuccess: state.similarStylesSuccess,
    staticProduct: state?.staticProduct || ownProps?.staticProduct,
    userData: state.userData,
    workingFilters: state.workingFilters,
    youMayAlsoLikeSuccess: state.youMayAlsoLikeSuccess,
  };
};

const mapDispatchToProps = dispatch => {
  return {
    fetchRentTheLookCarousel: productId => {
      dispatch(productsActions.fetchRentTheLook(productId));
    },
    fetchSimilarStylesCarousel: (productId, userData, workingFilters, title) => {
      dispatch(productsActions.fetchSimilarStyles(productId, title));
    },
    fetchYouMayAlsoLikeCarousel: (productId, userData, workingFilters) => {
      dispatch(productsActions.fetchYouMayAlsoLike(productId, userData, workingFilters));
    },
    fetchCollectionCarousel: (collectionId, category) => {
      dispatch(productsActions.fetchCollectionCarousel(collectionId, category));
    },
    fetchRecommendationCarousel: (query, title) => {
      dispatch(productsActions.fetchRecommendationCarousel(query, title));
    },
    fetchDesignerRecommendationsCarousel: (query, title) => {
      dispatch(
        productsActions.fetchRecCarousel(query, "designerRecommendationsSuccess", title, "DESIGNER_RECOMMENDATIONS")
      );
    },
    fetchStylePersonaRecommendationsCarousel: (query, title) => {
      dispatch(
        productsActions.fetchRecCarousel(
          query,
          "stylePersonaRecommendationsSuccess",
          title,
          "STYLE_PERSONA_RECOMMENDATIONS"
        )
      );
    },
    fetchNecklineRecommendationsCarousel: (query, title) => {
      dispatch(
        productsActions.fetchRecCarousel(
          query,
          "necklineRecommendationsSuccess",
          title,
          carouselTypes.NECKLINE_RECOMMENDATIONS
        )
      );
    },
    fetchEmbellishmentRecommendationsCarousel: (query, title) => {
      dispatch(
        productsActions.fetchRecCarousel(
          query,
          "embellishmentRecommendationsSuccess",
          title,
          "EMBELLISHMENT_RECOMMENDATIONS"
        )
      );
    },
    fetchSimilarDesignersRecommendationsCarousel: (query, title, isClearance = false) => {
      dispatch(
        productsActions.fetchSimilarDesignersCarousel(
          query,
          "similarDesignersRecommendationsSuccess",
          title,
          "SIMILAR_DESIGNERS_RECOMMENDATIONS",
          isClearance
        )
      );
    },
    fetchClearanceCarousel: category => {
      dispatch(productsActions.fetchClearanceCarousel(category));
    },
  };
};

export default compose(
  withOnLoadFlagsAndExperiments(flagsAndExperimentNames.SACT_SF_SACT_567_RECENTLY_VIEWED_PDP_CAROUSEL),
  withFeatureFlags(Flags.PDP_HOW_WE_STYLED_IT, Flags.VISUALLY_SIMILAR_TO_FLAG),
  connect(mapStateToProps, mapDispatchToProps)
)(PDPCarousels);
